import { useRole } from '@/hooks/useRole'
import { ArticleService } from '@/types'
import { Autocomplete, Button, Group, Modal, NumberInput, Select, Stack, TextInput } from '@mantine/core'
import { useEffect, useState } from 'react'

export default function ArticleFormModal({
  opened,
  onClose,
  onSubmit,
  articleServices,
  isCompanyCustomer,
  existingReferences = [],
  title,
  submitLabel,
}: {
  opened: boolean
  onClose: () => void
  onSubmit: (data: {
    articleName: string
    serviceName: string
    price: number
    instructions: string
    reference: string
  }) => void
  articleServices: ArticleService[]
  isCompanyCustomer: boolean
  existingReferences?: string[]
  title: string
  submitLabel: string
}) {
  const { isBoutique } = useRole()
  const [articleName, setArticleName] = useState('')
  const [selectedService, setSelectedService] = useState<string | null>(null)
  const [price, setPrice] = useState<number | string>('')
  const [instructions, setInstructions] = useState('')
  const [reference, setReference] = useState('')

  const uniqueArticleNames = Array.from(new Set((articleServices || []).map((s) => s.article_name))).sort()
  const filteredServices = (articleServices || []).filter((s) => s.article_name === articleName)
  const currentService = filteredServices.find((s) => s.service_name === selectedService)
  const suggestedReferences = Array.from(new Set(existingReferences.filter((r) => r && r.trim().length > 0)))

  useEffect(() => {
    if (currentService) {
      setPrice(currentService.price)
    } else {
      setPrice('')
    }
  }, [currentService])

  const isMissingPrice = price === '' || price === null || price === undefined

  const reset = () => {
    setArticleName('')
    setSelectedService(null)
    setPrice('')
    setInstructions('')
    setReference('')
  }

  useEffect(() => {
    if (opened) reset()
  }, [opened])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = () => {
    if (!articleName.trim() || !selectedService || isMissingPrice) return

    onSubmit({
      articleName,
      serviceName: selectedService,
      price: typeof price === 'string' ? parseFloat(price) : price,
      instructions,
      reference,
    })
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={title} centered>
      <Stack>
        <Autocomplete
          label="Article"
          placeholder="Ex: Veste, Pantalon..."
          data={uniqueArticleNames}
          value={articleName}
          onChange={(val) => {
            setArticleName(val)
            setSelectedService(null)
          }}
          onOptionSubmit={(val) => setArticleName(val)}
          onFocus={() => setArticleName('')}
          data-autofocus
        />
        <Select
          label="Service"
          placeholder="Choisir un service"
          data={filteredServices.map((s) => s.service_name)}
          value={selectedService}
          onChange={setSelectedService}
        />
        <NumberInput
          label="Prix (€)"
          value={price}
          onChange={setPrice}
          min={0}
          decimalScale={2}
          fixedDecimalScale
          readOnly={isBoutique}
        />
        <TextInput
          label="Instructions"
          placeholder="Instructions (optionnel)"
          value={instructions}
          onChange={(e) => setInstructions(e.currentTarget.value)}
        />
        {isCompanyCustomer && (
          <Stack gap="xs">
            <TextInput
              label="Référence"
              placeholder="Numero de commande ou nom du client"
              value={reference}
              onChange={(e) => setReference(e.currentTarget.value)}
            />
            {suggestedReferences.length > 0 && (
              <Group gap="xs">
                {suggestedReferences.map((suggestion) => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="default"
                    size="compact-xs"
                    radius="xl"
                    onClick={() => setReference(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </Group>
            )}
          </Stack>
        )}
        <Button onClick={handleSubmit} disabled={!articleName.trim() || !selectedService || isMissingPrice}>
          {submitLabel}
        </Button>
      </Stack>
    </Modal>
  )
}
